import { z } from "zod";
import type { CheckInView } from "./checkin.js";
import type { CliReport } from "./cli.js";
import { Level } from "./wire.js";

/**
 * Services and their activity: every signal Pagerless received, per app and service, whether or not
 * it opened or changed a problem. Core keeps a bounded log (Activity); api merges what core saw with
 * the latest `npx pagerless connect` report and the app's scheduled jobs into one catalog. Pure.
 */

/** What a signal was, in one word the screen can name. */
export const SignalKind = z.enum([
  "error",
  "http_errors",
  "alarm",
  "alarm_ok",
  "aws_event",
  "task_stopped",
  "task_running",
  "deploy",
  "checkin",
  "checkin_failed",
  "event",
  "resolved",
]);
export type SignalKind = z.infer<typeof SignalKind>;

/** Plain words for each kind. */
export const SIGNAL_KIND_TEXT: Record<SignalKind, string> = {
  error: "Error",
  http_errors: "Failed requests",
  alarm: "Alarm",
  alarm_ok: "Alarm OK",
  aws_event: "AWS event",
  task_stopped: "Task stopped",
  task_running: "Task running",
  deploy: "Deploy",
  checkin: "Job ran",
  checkin_failed: "Job failed",
  event: "Event",
  resolved: "Back to normal",
};

/** Bounds on the activity log, so storage can't grow without limit. */
export const Activity = {
  /** Newest signals kept per (app, service). */
  keepPerService: 200,
  /** Signals older than this are dropped. */
  ttlDays: 14,
  /** Distinct services per app; more names than this are kept under "other". */
  maxServicesPerProject: 300,
  readDefault: 50,
  readMax: 200,
} as const;

/** One signal as received (tags scrubbed, text capped). Untrusted text: only ever displayed. */
export const ServiceSignal = z.object({
  id: z.number().int(),
  at: z.number().int(),
  service: z.string().max(100),
  env: z.string().max(40),
  kind: SignalKind,
  title: z.string().max(200),
  /** How loud the source asked for it to be, when it said. */
  level: Level.nullable(),
  count: z.number().int().positive(),
  tags: z.record(z.string().max(64), z.string().max(256)),
  /** The problem it opened, joined or closed; null when it touched none. */
  problemId: z.string().nullable(),
});
export type ServiceSignal = z.infer<typeof ServiceSignal>;

/** Something that watches a service: a CloudWatch alarm, an EventBridge rule. */
export const Watcher = z.object({ kind: z.enum(["alarm", "aws_rule"]), name: z.string().max(256) });
export type Watcher = z.infer<typeof Watcher>;

/** What core knows about one service of one app (AccountDO.serviceFacts). */
export const ServiceFacts = z.object({
  projectId: z.string(),
  service: z.string().max(100),
  firstAt: z.number().int(),
  lastAt: z.number().int(),
  lastKind: SignalKind.nullable(),
  /** Open problems that came from this service. */
  openProblems: z.number().int().nonnegative(),
  /** Signals in the last 24 hours. */
  signals24h: z.number().int().nonnegative(),
  /** Where it runs, as the signals say ("ecs", "lambda", "aws"); null when they don't. */
  platform: z.string().max(40).nullable(),
  watchedBy: z.array(Watcher).max(50),
  /** Heartbeats, when the service sends them. */
  heartbeat: z.object({ lastAt: z.number().int(), online: z.boolean() }).nullable(),
});
export type ServiceFacts = z.infer<typeof ServiceFacts>;

/** Where a service runs: the services map groups by this. */
export const RunsOn = z.enum([
  "ecs",
  "lambda",
  "cloudflare",
  "vercel",
  "railway",
  "server",
  "browser",
  "device",
  "jobs",
  "aws",
  "unknown",
]);
export type RunsOn = z.infer<typeof RunsOn>;

export const RUNS_ON_TEXT: Record<RunsOn, string> = {
  ecs: "AWS ECS",
  lambda: "AWS Lambda",
  cloudflare: "Cloudflare",
  vercel: "Vercel",
  railway: "Railway",
  server: "Servers",
  browser: "Browser and extensions",
  device: "Phones",
  jobs: "Scheduled jobs",
  aws: "AWS alarms and events",
  unknown: "Other",
};

/** `failing`: a problem is open. `silent`: heartbeats stopped. `ok`: signals, nothing open. `none`: no signal yet. */
export const ServiceStatus = z.enum(["ok", "failing", "silent", "none"]);
export type ServiceStatus = z.infer<typeof ServiceStatus>;

/** One row of the Services list (GET /v1/projects/:id/services). */
export const ServiceView = z.object({
  /** Plain name: the connect unit's name when known, else the name signals arrive under. */
  name: z.string().max(100),
  /** Every service name its signals arrive under (its activity is all of them). */
  keys: z.array(z.string().max(100)).max(30),
  runsOn: RunsOn,
  /** "Go · Gin · ECS"; null when unknown. */
  runtime: z.string().max(200).nullable(),
  status: ServiceStatus,
  lastSignalAt: z.number().int().nullable(),
  openProblems: z.number().int().nonnegative(),
  signals24h: z.number().int().nonnegative(),
  watchedBy: z.array(Watcher).max(50),
  /** Found by `npx pagerless connect`. */
  fromConnect: z.boolean(),
});
export type ServiceView = z.infer<typeof ServiceView>;

export const ServicesView = z.object({
  services: z.array(ServiceView),
  /** When `connect` last reported this app; null when it never did. */
  connectedAt: z.number().int().nullable(),
});
export type ServicesView = z.infer<typeof ServicesView>;

export const ServiceActivityView = z.object({
  service: ServiceView,
  signals: z.array(ServiceSignal),
});
export type ServiceActivityView = z.infer<typeof ServiceActivityView>;

// ───────────── merging ─────────────

type Unit = CliReport["units"][number];

const RUNTIME_TEXT: Record<Unit["runtime"], string> = {
  node: "Node",
  go: "Go",
  cloudflare: "JavaScript",
  python: "Python",
  aws: "AWS",
};

const DEPLOY_TEXT: Record<string, string> = {
  ecs: "ECS",
  lambda: "Lambda",
  "cloudflare-worker": "Cloudflare Workers",
  "cloudflare-pages": "Cloudflare Pages",
  vercel: "Vercel",
  railway: "Railway",
};

const DEPLOY_RUNS_ON: Record<string, RunsOn> = {
  ecs: "ecs",
  lambda: "lambda",
  "cloudflare-worker": "cloudflare",
  "cloudflare-pages": "cloudflare",
  vercel: "vercel",
  railway: "railway",
};

const DEVICE = /\b(swift|kotlin|android|ios|flutter|react native|expo)\b/i;
const BROWSER = /\b(extension|browser|vite|spa)\b/i;

const cap = (s: string) => (s ? s[0]?.toUpperCase() + s.slice(1) : s);

/** "Go · Gin · ECS" */
export function runtimeText(u: Pick<Unit, "runtime" | "framework" | "deploy">): string {
  const parts = [RUNTIME_TEXT[u.runtime]];
  if (u.framework) parts.push(cap(u.framework));
  const d = u.deploy ? DEPLOY_TEXT[u.deploy.kind] : undefined;
  if (d) parts.push(d);
  return parts.join(" · ").slice(0, 200);
}

export function runsOnOfUnit(u: Pick<Unit, "runtime" | "framework" | "deploy">): RunsOn {
  const byDeploy = u.deploy ? DEPLOY_RUNS_ON[u.deploy.kind] : undefined;
  if (byDeploy) return byDeploy;
  if (u.framework && DEVICE.test(u.framework)) return "device";
  if (u.framework && BROWSER.test(u.framework)) return "browser";
  if (u.runtime === "cloudflare") return "cloudflare";
  if (u.runtime === "aws") return "aws";
  return "server";
}

function runsOnOfPlatform(platform: string | null): RunsOn {
  const r = RunsOn.safeParse(platform);
  return r.success ? r.data : "unknown";
}

/** The activity key of a scheduled job. */
export const jobKey = (slug: string) => `job:${slug}`;

/** "job:nightly-backup" → "nightly-backup (job)"; other names as they are. */
export function serviceLabel(service: string): string {
  return service.startsWith("job:") ? `${service.slice(4)} (job)` : service;
}

function statusOf(open: number, heartbeat: ServiceFacts["heartbeat"], lastAt: number | null): ServiceStatus {
  if (open > 0) return "failing";
  if (heartbeat && !heartbeat.online) return "silent";
  return lastAt === null ? "none" : "ok";
}

function addWatchers(into: Watcher[], from: Watcher[]): void {
  for (const w of from) {
    if (into.length >= 50) return;
    if (!into.some((x) => x.kind === w.kind && x.name === w.name)) into.push(w);
  }
}

/**
 * One catalog from three sources: services core has seen (heartbeats, events, alarms), the units the
 * last `connect` found (with what they run on), and the app's scheduled jobs. A signal under a unit's
 * deploy name (its ECS service, its Lambda function) belongs to that unit. Failing first, then by name.
 */
export function mergeServices(input: {
  facts: ServiceFacts[];
  units: ReadonlyArray<Unit>;
  jobs: ReadonlyArray<Pick<CheckInView, "slug" | "name" | "state" | "lastAt">>;
}): ServiceView[] {
  const views: ServiceView[] = [];
  const owner = new Map<string, ServiceView>();
  const claim = (key: string, v: ServiceView) => {
    const k = key.toLowerCase();
    if (!owner.has(k)) {
      owner.set(k, v);
      if (!v.keys.includes(key) && v.keys.length < 30) v.keys.push(key);
    }
  };

  for (const u of input.units) {
    const v: ServiceView = {
      name: u.name,
      keys: [],
      runsOn: runsOnOfUnit(u),
      runtime: runtimeText(u),
      status: "none",
      lastSignalAt: null,
      openProblems: 0,
      signals24h: 0,
      watchedBy: [],
      fromConnect: true,
    };
    if (owner.has(u.name.toLowerCase())) continue;
    views.push(v);
    claim(u.name, v);
    for (const n of u.deploy?.names ?? []) claim(n, v);
  }
  for (const j of input.jobs) {
    const v: ServiceView = {
      name: j.name,
      keys: [],
      runsOn: "jobs",
      runtime: "Scheduled job",
      status: j.state === "late" || j.state === "failed" ? "failing" : j.lastAt === null ? "none" : "ok",
      lastSignalAt: j.lastAt,
      openProblems: 0,
      signals24h: 0,
      watchedBy: [],
      fromConnect: false,
    };
    views.push(v);
    claim(jobKey(j.slug), v);
  }

  const heartbeats = new Map<ServiceView, ServiceFacts["heartbeat"]>();
  for (const f of input.facts) {
    let v = owner.get(f.service.toLowerCase());
    if (!v) {
      v = {
        name: f.service.startsWith("job:") ? serviceLabel(f.service) : f.service,
        keys: [],
        runsOn: f.service.startsWith("job:") ? "jobs" : runsOnOfPlatform(f.platform),
        runtime: null,
        status: "none",
        lastSignalAt: null,
        openProblems: 0,
        signals24h: 0,
        watchedBy: [],
        fromConnect: false,
      };
      views.push(v);
      claim(f.service, v);
    }
    v.lastSignalAt = Math.max(v.lastSignalAt ?? 0, f.lastAt);
    v.openProblems += f.openProblems;
    v.signals24h += f.signals24h;
    addWatchers(v.watchedBy, f.watchedBy);
    if (f.heartbeat) {
      const prev = heartbeats.get(v);
      // Any instance online is the service online.
      heartbeats.set(v, {
        lastAt: Math.max(prev?.lastAt ?? 0, f.heartbeat.lastAt),
        online: (prev?.online ?? false) || f.heartbeat.online,
      });
    }
  }
  for (const v of views) {
    if (v.runsOn === "jobs" && v.status === "failing") {
      v.openProblems = Math.max(1, v.openProblems);
      continue;
    }
    v.status = statusOf(v.openProblems, heartbeats.get(v) ?? null, v.lastSignalAt);
  }
  const rank: Record<ServiceStatus, number> = { failing: 0, silent: 1, ok: 2, none: 3 };
  return views.sort((a, b) => rank[a.status] - rank[b.status] || a.name.localeCompare(b.name));
}
